/**
 * Fraction additions and subtractions written in learner-confirmed text
 * (D-17 topic, D-21 concept-first). Shared, byte-identical, by the
 * `material-learning` Edge Function and the app
 * (`src/app/screens/materials/fraction-expressions.ts`): the app uses it only
 * to decide whether to ask the learner to confirm the topic; the server
 * decides again with the same code. Functions deploy separately and cannot
 * import from src/, so the two files are kept equal by a test.
 *
 * An expression is `ambiguous` when the text around it shows that it is part
 * of something larger than "a/b ± c/d": a mixed number (`1 1/2 + 2/3`), a
 * sign or another term before it, or a further operation after it
 * (`3/4 + 1/8 × 2`, `3/4 + 1/8 + 1/2`). Its value is then not the value of the
 * written exercise, so it never identifies the topic on its own and its
 * computed answer is not trusted.
 */

export interface Fraction {
  readonly n: number
  readonly d: number
}

export type Op = '+' | '-'

export interface ParsedExpression {
  readonly a: Fraction
  readonly b: Fraction
  readonly op: Op
  readonly ref: string
  readonly ambiguous: boolean
}

export const MAX_TERM = 100

const EXPRESSION = /(\d{1,3})\s*\/\s*(\d{1,3})\s*([+\-−–])\s*(\d{1,3})\s*\/\s*(\d{1,3})/gu
const FRACTION = /(\d{1,3})\s*\/\s*(\d{1,3})/gu

/** Text right before the match: a mixed number, a sign, or a term with an operator. */
const AMBIGUOUS_BEFORE = /(?:\d[ \t]+|[\d)]\s*[+\-−–×x*÷:/]\s*|[+\-−–]\s*)$/u
/** Text right after the match: a further operation on a number or bracket. */
const AMBIGUOUS_AFTER = /^\s*[+\-−–×x*÷:/]\s*[\d(]/u

function gcd(a: number, b: number): number {
  let x = Math.abs(a)
  let y = Math.abs(b)
  while (y !== 0) [x, y] = [y, x % y]
  return x === 0 ? 1 : x
}

export const lcm = (a: number, b: number) => (a / gcd(a, b)) * b

export function simplify(f: Fraction): Fraction {
  const g = gcd(f.n, f.d)
  return { n: f.n / g, d: f.d / g }
}

/** Equal values (3/4 and 6/8 are the same). */
export const same = (x: Fraction, y: Fraction) => x.n * y.d === y.n * x.d

export function compute(e: {
  readonly a: Fraction
  readonly b: Fraction
  readonly op: Op
}): Fraction {
  const d = lcm(e.a.d, e.b.d)
  const n =
    e.op === '+'
      ? e.a.n * (d / e.a.d) + e.b.n * (d / e.b.d)
      : e.a.n * (d / e.a.d) - e.b.n * (d / e.b.d)
  return { n, d }
}

/** Expressions in the given texts, in order, each marked ambiguous or not. */
export function findFractionExpressions(
  texts: readonly { readonly ref: string; readonly text: string }[],
): ParsedExpression[] {
  const out: ParsedExpression[] = []
  for (const { ref, text } of texts) {
    const t = text.normalize('NFC')
    for (const m of t.matchAll(EXPRESSION)) {
      const [an, ad, op, bn, bd] = [m[1], m[2], m[3], m[4], m[5]].map((v) => v ?? '')
      const a = { n: Number(an), d: Number(ad) }
      const b = { n: Number(bn), d: Number(bd) }
      const sign: Op = op === '+' ? '+' : '-'
      const valid =
        [a.n, a.d, b.n, b.d].every((v) => Number.isInteger(v) && v <= MAX_TERM) &&
        a.d > 0 &&
        b.d > 0 &&
        compute({ a, b, op: sign }).n >= 0
      if (!valid) continue
      const start = m.index
      const end = start + m[0].length
      const ambiguous =
        AMBIGUOUS_BEFORE.test(t.slice(0, start)) || AMBIGUOUS_AFTER.test(t.slice(end))
      out.push({ a, b, op: sign, ref, ambiguous })
    }
  }
  return out
}

/** Every fraction written anywhere in the texts (the source's distinctive data). */
export function findFractions(
  texts: readonly { readonly ref: string; readonly text: string }[],
): Fraction[] {
  const out: Fraction[] = []
  for (const { text } of texts) {
    for (const m of text.normalize('NFC').matchAll(FRACTION)) {
      const f = { n: Number(m[1]), d: Number(m[2]) }
      if (f.d > 0) out.push(f)
    }
  }
  return out
}

/** The supported topic is identified only by at least one unambiguous expression. */
export function hasFractionTopic(
  texts: readonly { readonly ref: string; readonly text: string }[],
): boolean {
  return findFractionExpressions(texts).some((e) => !e.ambiguous)
}
